import Icons from "@/assets/icons";
import dayjs from "dayjs";
import { OfferCardLayoutData, OfferCardProps } from "./types";
import { ActivityIndicator } from "react-native";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
export class OfferCardLayoutDataFactory {
  static createLayoutData(
    data: OfferCardProps["data"],
    onClaim?: () => void,
    onStartMonitoring?: () => void,
    onDownload?: () => void,
    isDownloading?: boolean,
  ): OfferCardLayoutData {
    switch (data.type) {
      case "classAction":
        return this.createClassActionLayout(
          data,
          onClaim,
          onDownload,
          isDownloading,
        );
      case "bank":
        return this.createBankLayout(data, onClaim);
      case "unclaimedMoney":
        return this.createUnclaimedMoneyLayout(
          data,
          onClaim,
          onStartMonitoring,
        );
      case "unclaimedProperty":
        return this.createUnclaimedPropertyLayout(onStartMonitoring);
      default:
        return this.createDefaultLayout(onStartMonitoring);
    }
  }

  private static createClassActionLayout(
    data: any,
    onClaim?: () => void,
    onDownload?: () => void,
    isDownloading?: boolean,
  ): OfferCardLayoutData {
    const daysLeft = dayjs(data.dueAt).diff(dayjs(), "day");
    const isExpired = daysLeft <= 0;
    const isClaimed = data.claimed;
    const canClaim = !isExpired && !isClaimed;

    // Wrap onClaim with tracking
    const trackedOnClaim =
      canClaim && onClaim
        ? () => {
            FBTrackingService.trackEvent(TRACKING_EVENTS.CLASS_ACTION_CLICKED, {
              eventData: {
                id: data.id,
                title: data.title,
                payout: data.payout,
                suitId: data.suitId,
              },
            });
            onClaim();
          }
        : undefined;

    // Wrap onDownload with tracking
    const trackedOnDownload =
      onDownload && !isDownloading
        ? () => {
            FBTrackingService.trackEvent(
              TRACKING_EVENTS.CLASS_ACTION_CLAIM_DOWNLOADED,
              {
                eventData: {
                  id: data.id,
                  title: data.title,
                  suitId: data.suitId,
                },
              },
            );
            onDownload();
          }
        : undefined;

    return {
      // Primary action is hidden for claimed/expired items
      actionTitle: !isExpired && !isClaimed ? "Claim" : undefined,
      onPress: trackedOnClaim,
      disabled: !canClaim && !isClaimed,
      amountText: `$${
        (data.payout / 100) % 1 === 0
          ? (data.payout / 100).toFixed(0)
          : (data.payout / 100).toFixed(2)
      }`,
      notes: [
        !isClaimed && {
          text: !isExpired ? `${daysLeft} days left` : "Expired",
          icon: <Icons.ClockGray />,
        },
        isClaimed && {
          text: "Mailed in",
          icon: <Icons.Mail width={18} height={18} />,
        },
      ].filter(Boolean),
      tags: [
        // If claimed, show download button as a tag instead of proof tag
        isClaimed
          ? {
              text: isDownloading ? "Loading..." : "Download",
              color: isDownloading ? "#6B7280" : "#000000",
              backgroundColor: isDownloading ? "#E5E7EB" : "#F4F5F6",
              icon: isDownloading ? (
                <ActivityIndicator size={12} color="#6B7280" />
              ) : (
                <Icons.Download width={14} height={14} color="#000000" />
              ),
              onPress: isDownloading ? undefined : trackedOnDownload,
            }
          : data.proof === false
            ? {
                text: "No proof",
                color: "#161D14",
                backgroundColor: "#F4F5F6",
                icon: <Icons.Check width={12} height={12} />,
              }
            : {
                text: "Proof",
                color: "#161D14",
                backgroundColor: "#F4F5F6",
                icon: <Icons.CheckCircleRed width={12} height={12} />,
              },
      ],
    };
  }

  private static createBankLayout(
    data: any,
    onClaim?: () => void,
  ): OfferCardLayoutData {
    return {
      actionTitle: "Claim",
      onPress: () => {
        FBTrackingService.trackEvent(TRACKING_EVENTS.BANK_OFFER_CLICKED, {
          eventData: {
            id: data.id,
            title: data.title,
            signUpBonus: data.signUpBonus,
            monthlyFee: data.monthlyFee,
          },
        });
        onClaim?.();
      },
      amountText: String(data.signUpBonus),
      notes: [
        data.minDeposit && {
          icon: <Icons.SearchCircleGray />,
          text: `Claim with a ${data.minDeposit} deposit`,
        },
      ],
      tags: [],
    };
  }

  private static createUnclaimedMoneyLayout(
    data: any,
    onClaim?: () => void,
    onStartMonitoring?: () => void,
  ): OfferCardLayoutData {
    const trackedOnPress =
      data.canClaim && onClaim
        ? () => {
            FBTrackingService.trackEvent(
              TRACKING_EVENTS.UNCLAIMED_MONEY_CLICKED,
              {
                eventData: {
                  type: data.type,
                  minAmount: data.minAmount,
                  maxAmount: data.maxAmount,
                  state: data.address?.state,
                  ownerName: data.ownerName,
                },
              },
            );
            onClaim();
          }
        : onStartMonitoring;

    return {
      actionTitle: "Claim",
      amountText: `$${data.minAmount}-$${data.maxAmount}`,
      notes: [
        {
          text: `Held in ${data.address.state} for ${data.ownerName}`,
          icon: <Icons.SearchCircleGray />,
        },
      ],
      tags: [],
      onPress: trackedOnPress,
    };
  }

  private static createUnclaimedPropertyLayout(
    onStartMonitoring?: () => void,
  ): OfferCardLayoutData {
    return {
      title: "Search for unclaimed money",
      actionTitle: "Claim",
      amountText: "Up to $300",
      notes: [
        {
          text: "Find lost checks shipped to your mailing address",
          icon: <Icons.SearchCircleGray />,
        },
      ],
      tags: [],
      onPress: onStartMonitoring,
    };
  }

  private static createDefaultLayout(
    onStartMonitoring?: () => void,
  ): OfferCardLayoutData {
    return {
      actionTitle: "Claim",
      notes: [],
      tags: [],
      onPress: onStartMonitoring,
    };
  }

}
